import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Schedule } from '../../types/project';

interface ComparisonData {
  schedules: Schedule[];
  comparison_data: {
    total_schedules: number;
    date_range: {
      earliest_start: string | null;
      latest_end: string | null;
    };
    activity_count_range: {
      min: number | null;
      max: number | null;
      total: number | null;
    };
    duration_range: {
      min: number | null;
      max: number | null;
      average: number | null;
    };
  };
  differences: Array<{
    schedule1_id: string;
    schedule1_name: string;
    schedule2_id: string;
    schedule2_name: string;
    start_date_difference_days: number | null;
    end_date_difference_days: number | null;
    duration_difference_days: number | null;
    activity_count_difference: number | null;
  }>;
}

const ScheduleComparison: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);
  const [selectedSchedules, setSelectedSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (location.state) {
      setComparisonData(location.state.comparisonData);
      setSelectedSchedules(location.state.selectedSchedules || []);
      setLoading(false);
    } else {
      setError('No comparison data available. Please select schedules to compare.');
      setLoading(false);
    }
  }, [location.state]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">Loading comparison...</div>
      </div>
    );
  }

  if (error || !comparisonData) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error || 'No comparison data available'}
        </div>
        <Link to="/projects" className="text-blue-600 hover:text-blue-800">
          Return to Projects
        </Link>
      </div>
    );
  }

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDifference = (value: number | null, unit: string = 'days'): string => {
    if (value === null) return 'N/A';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value} ${unit}`;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'Current': return 'bg-green-100 text-green-800';
      case 'Baseline': return 'bg-blue-100 text-blue-800';
      case 'Historical': return 'bg-gray-100 text-gray-800';
      case 'What-If': return 'bg-purple-100 text-purple-800';
      case 'ready': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Schedule Comparison</h1>
            <p className="text-gray-500">
              Comparing {comparisonData.schedules.length} schedules
            </p>
          </div>
          <Link
            to={`/projects/${selectedSchedules[0]?.project_id}/schedules`}
            className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm"
          >
            Back to Schedules
          </Link>
        </div>
      </div>

      {/* Comparison Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Schedules</p>
          <p className="text-2xl font-bold text-gray-900">{comparisonData.comparison_data.total_schedules}</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Date Range</p>
          <p className="text-sm text-gray-700">
            {formatDate(comparisonData.comparison_data.date_range.earliest_start)} - {formatDate(comparisonData.comparison_data.date_range.latest_end)}
          </p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Activity Count Range</p>
          <p className="text-sm text-gray-700">
            {comparisonData.comparison_data.activity_count_range.min} - {comparisonData.comparison_data.activity_count_range.max}
          </p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Duration Range</p>
          <p className="text-sm text-gray-700">
            {comparisonData.comparison_data.duration_range.min} - {comparisonData.comparison_data.duration_range.max} days
          </p>
        </div>
      </div>

      {/* Schedule Details Comparison */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-800">Schedule Details</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Schedule
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  End Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Activities
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {comparisonData.schedules.map((schedule) => (
                <tr key={schedule.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{schedule.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(schedule.status || '')}`}>
                      {schedule.status === 'ready' ? 'Ready' : 
                       schedule.status === 'completed' ? 'Completed' : 
                       schedule.status || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(schedule.start_date || null)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(schedule.end_date || null)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {schedule.duration ? `${schedule.duration} days` : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {schedule.activity_count || 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Differences Analysis */}
      {comparisonData.differences.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-800">Differences Analysis</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Comparison
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Start Date Diff
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    End Date Diff
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration Diff
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activity Count Diff
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {comparisonData.differences.map((difference, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {difference.schedule1_name} vs {difference.schedule2_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={difference.start_date_difference_days && difference.start_date_difference_days > 0 ? 'text-red-600' : 'text-green-600'}>
                        {formatDifference(difference.start_date_difference_days)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={difference.end_date_difference_days && difference.end_date_difference_days > 0 ? 'text-red-600' : 'text-green-600'}>
                        {formatDifference(difference.end_date_difference_days)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={difference.duration_difference_days && difference.duration_difference_days > 0 ? 'text-red-600' : 'text-green-600'}>
                        {formatDifference(difference.duration_difference_days)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={difference.activity_count_difference && difference.activity_count_difference > 0 ? 'text-blue-600' : 'text-gray-600'}>
                        {formatDifference(difference.activity_count_difference, 'activities')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No differences message */}
      {comparisonData.differences.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-700 mb-2">No Differences Found</h3>
          <p className="text-gray-500">The selected schedules appear to be identical in their key metrics.</p>
        </div>
      )}
    </div>
  );
};

export default ScheduleComparison; 

